import { dynamicBaseQuery } from "@/api/DynamicBaseQuery";
import type { IApiResponse, IApiPaginatedResponse } from "@/types";
import { createApi } from "@reduxjs/toolkit/query/react";
import type {
  ModuleType,
  ScopeOptionsByType,
  Workflow,
  WorkflowEventTypeGroup,
} from "@/pages/(home)/(company)/workflows/builder_v2/types";
import type {
  IWorkflowAuditEntry,
  IWorkflowInstance,
  IWorkflowInstanceByEntityResponse,
  IWorkflowInstanceStats,
  IWorkflowRequest,
  IWorkflowVersion,
  IWorkflowVersionListMeta,
  WorkflowIssueFilter,
} from "@/types/(company)/workflow.interface";
import type {
  IStudioWorkflowListItem,
  IWorkflowModuleCounts,
  IWorkflowGraphView,
  IUpsertWorkflowGraphPayload,
  ITestTraceResult,
  ITestWorkflowStepPayload,
} from "@/pages/(home)/(company)/studio/workflows/_components/workflow-types";
import { APIEndpoints } from "@/constants/apiEndpoints";

type TPreviewWorkflowResponse = IApiResponse<{
  leaveRequest: {
    userId: number;
    companyId: number;
    leaveTypeId: number | null;
    approvedBy: null;
    startDate: string | null;
    endDate: string | null;
    status: string;
    reasonOfLeave: string | null;
    reasonOfRejection: null;
    attachment: null;
    leaveType: { id: number; name: string } | null;
    user: {
      id: number;
      firstName: string;
      lastName: string;
      profileImage: string | null;
    } | null;
  } | null;
  approvalSteps: {
    startingPoint: {
      requester: {
        id: number;
        firstName: string;
        lastName: string;
        profileImage: string | null;
      };
      condition: {
        leaveTypeName: string;
        startDate: string;
        endDate: string;
      };
    };
    totalSteps: number;
    currentStep: number;
    instanceStatus: string;
    steps: {
      stepOrder: number;
      stepName: string;
      positionName: string;
      approver: {
        id: number;
        firstName: string;
        lastName: string;
        profileImage: string | null;
      } | null;
      branch: string | null;
      status: string;
      isCurrent: boolean;
      actionAt: string | null;
      comments: string | null;
    }[];
  } | null;
}>;

type TPreviewWorkflowParams = {
  module: "leave_management" | "time_attendance" | "asset_management";
  leaveTypeId?: number;
  regularizationType?: string;
  assetCategory?: string;
  startDate?: string;
  endDate?: string;
  reasonOfLeave?: string;
};

export const companyWorkflowApi = createApi({
  reducerPath: "companyWorkflowApi",
  baseQuery: dynamicBaseQuery,
  tagTypes: [
    "getWorkflowById",
    "getWorkflowTemplatesByModule",
    "getPaginatedWorkflowTemplates",
    "getWorkflowRequests",
    "getAllWorkflowRequests",
    "getWorkflowInstances",
    "getWorkflowInstanceStats",
    "getWorkflowInstanceAudit",
    "getGroupedEventTypes",
    "getScopeOptions",
    "WorkflowInstance",
    "getStudioWorkflows",
    "getStudioWorkflowCounts",
    "getWorkflowGraph",
  ],
  endpoints: (builder) => ({
    // Builder graph read — envelope + node/edge graph, ready to hydrate.
    getWorkflowGraph: builder.query<
      IApiResponse<IWorkflowGraphView>,
      string | number
    >({
      query: (id) => ({
        url: `/workflows/templates/${id}/graph`,
        method: "GET",
      }),
      providesTags: (_result, _error, id) => [
        { type: "getWorkflowGraph", id: String(id) },
      ],
    }),
    // Create or update a workflow from the builder graph (draft or publish).
    upsertWorkflowGraph: builder.mutation<
      IApiResponse<{ id: number }>,
      IUpsertWorkflowGraphPayload
    >({
      query: (body) => ({
        url: `/workflows/templates/upsert-graph`,
        method: "POST",
        body,
      }),
      invalidatesTags: (result, _error, body) => [
        { type: "getWorkflowGraph", id: String(body.id ?? result?.data.id) },
        "getStudioWorkflows",
        "getStudioWorkflowCounts",
        // A Studio save also mutates the underlying template, so the legacy
        // Workflows-page listings must refresh too or they show stale data.
        { type: "getWorkflowById", id: String(body.id ?? result?.data.id) },
        "getWorkflowTemplatesByModule",
        "getPaginatedWorkflowTemplates",
      ],
    }),
    // Dry-run trace for the builder's "Test" button. Stateless — no cache
    // invalidation, nothing else in the store changes.
    testWorkflowStep: builder.mutation<
      IApiResponse<ITestTraceResult>,
      { id: string | number } & ITestWorkflowStepPayload
    >({
      query: ({ id, ...body }) => ({
        url: `/workflows/templates/${id}/test-step`,
        method: "POST",
        body,
      }),
    }),
    // ─── Studio (graph builder) ────────────────────────────────────────────────
    // Template counts per module — powers the Studio category picker cards.
    getStudioWorkflowCounts: builder.query<
      IApiResponse<IWorkflowModuleCounts>,
      { status?: string } | void
    >({
      query: (params) => ({
        url: `/workflows/templates/counts`,
        method: "GET",
        params: params ?? {},
      }),
      providesTags: ["getStudioWorkflowCounts"],
    }),
    // Paginated template list for a module, shaped for the Studio workflow cards.
    getStudioWorkflows: builder.query<
      IApiPaginatedResponse<IStudioWorkflowListItem[]>,
      {
        module?: string;
        search?: string;
        status?: string;
        sort?: string;
        page?: number;
        limit?: number;
      }
    >({
      query: (params) => ({
        url: `/workflows/templates`,
        method: "GET",
        params,
      }),
      providesTags: ["getStudioWorkflows"],
    }),
    getWorkflowById: builder.query<IApiResponse<Workflow>, string>({
      query: (id) => ({
        url: `/workflows/templates/${id}`,
        method: "GET",
      }),
      providesTags: (_result, _error, id) => [{ type: "getWorkflowById", id }],
    }),
    getWorkflowVersionById: builder.query<
      IApiResponse<Workflow>,
      { id: string; versionId: string | number }
    >({
      query: ({ id, versionId }) => ({
        url: `/workflows/templates/${id}/versions/${versionId}`,
        method: "GET",
      }),
    }),
    getWorkflowVersions: builder.query<
      IApiResponse<IWorkflowVersion[]> & { meta?: IWorkflowVersionListMeta },
      string
    >({
      query: (id) => ({
        url: `/workflows/templates/${id}/versions`,
        method: "GET",
      }),
    }),
    getWorkflowTemplatesByModule: builder.query<
      IApiResponse<Workflow[]>,
      ModuleType
    >({
      query: (module) => ({
        url: `/workflows/templates/detailed?module=${module}`,
        method: "GET",
      }),
      providesTags: ["getWorkflowTemplatesByModule"],
    }),
    getPaginatedWorkflowTemplates: builder.query<
      IApiPaginatedResponse<Workflow[]>,
      {
        module:        ModuleType;
        search?:       string;
        page?:         number;
        limit?:        number;
        status?:       string;
        branchId?:     number;
        departmentId?: number;
        eventTypeId?:  number;
      }
    >({
      query: (params) => ({
        url: `/workflows/templates`,
        method: "GET",
        params,
      }),
      providesTags: ["getPaginatedWorkflowTemplates"],
    }),
    getGroupedEventTypes: builder.query<
      IApiResponse<WorkflowEventTypeGroup[]>,
      void
    >({
      query: () => ({
        url: `/workflows/helpers/event-types`,
        method: "GET",
      }),
      providesTags: ["getGroupedEventTypes"],
    }),
    getScopeOptions: builder.query<IApiResponse<ScopeOptionsByType>, void>({
      query: () => ({
        url: `/workflows/helpers/scope-options`,
        method: "GET",
      }),
      providesTags: ["getScopeOptions"],
    }),
    previewApplicableWorkflow: builder.query<
      TPreviewWorkflowResponse,
      TPreviewWorkflowParams
    >({
      query: (params) => ({
        url: APIEndpoints.MySpace.workflow.previewApplicableWorkflow,
        method: "GET",
        params,
      }),
    }),
    /**
     * Company/HR variant — previews the workflow for a TARGET EMPLOYEE
     * (`userId`), not the calling admin. Used by the "Apply Leave for
     * Employee" form's Approval Flow tab.
     */
    previewApplicableWorkflowForEmployee: builder.query<
      TPreviewWorkflowResponse,
      TPreviewWorkflowParams & { userId: number }
    >({
      query: (params) => ({
        url: APIEndpoints.MySpace.workflow.previewApplicableWorkflowForEmployee,
        method: "GET",
        params,
      }),
    }),
    /**
     * Returns the most-recent workflow instance for a given entity together
     * with a shaped `approvalSteps` payload for ApprovalFlowSidebar.
     * Generic: works for any entityType (overtime_requests, leave_management, …).
     * Both fields are null when no workflow instance exists.
     */
    getWorkflowInstanceByEntity: builder.query<
      IApiResponse<IWorkflowInstanceByEntityResponse>,
      { entityType: string; entityId: number | string }
    >({
      query: ({ entityType, entityId }) => ({
        url: APIEndpoints.MySpace.workflow.getWorkflowInstanceByEntity(
          entityType,
          entityId,
        ),
        method: "GET",
      }),
      providesTags: (_result, _error, { entityType, entityId }) => [
        { type: "WorkflowInstance", id: `${entityType}:${entityId}` },
      ],
    }),
    upsertWorkflow: builder.mutation<IApiResponse<Workflow>, Workflow>({
      query: (workflow) => ({
        url: `/workflows/templates/upsert`,
        method: "POST",
        body: workflow,
      }),
      invalidatesTags: (result, __, workflow) => [
        { type: "getWorkflowById", id: result?.data.id ?? workflow.id },
        "getWorkflowTemplatesByModule",
        "getPaginatedWorkflowTemplates",
      ],
    }),
    previewWorkflowConflicts: builder.mutation<
      IApiResponse<{
        branchId: number | null;
        conflicts: Array<{
          id: number;
          name: string;
          module: string;
          branch: { id: number; name: string } | null;
          conditionType: string | null;
          conditionId: number | null;
          scopeType: string | null;
          scopeIds: number[];
        }>;
      }>,
      Partial<Workflow>
    >({
      query: (workflow) => ({
        url: `/workflows/templates/preview-conflicts`,
        method: "POST",
        body: workflow,
      }),
    }),
    activateWorkflowTemplate: builder.mutation<
      IApiResponse<Workflow>,
      { id: string | number }
    >({
      query: ({ id }) => ({
        url: `/workflows/templates/${id}/activate`,
        method: "POST",
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: "getWorkflowById", id: String(id) },
        "getWorkflowTemplatesByModule",
        "getPaginatedWorkflowTemplates",
        "getStudioWorkflows",
        "getStudioWorkflowCounts",
      ],
    }),
    deactivateWorkflowTemplate: builder.mutation<
      IApiResponse<Workflow>,
      { id: string | number }
    >({
      query: ({ id }) => ({
        url: `/workflows/templates/${id}/deactivate`,
        method: "POST",
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: "getWorkflowById", id: String(id) },
        "getWorkflowTemplatesByModule",
        "getPaginatedWorkflowTemplates",
        "getStudioWorkflows",
        "getStudioWorkflowCounts",
        // The builder reads the graph view — refresh it so its status chip
        // doesn't keep showing "Published" after an unpublish.
        { type: "getWorkflowGraph", id: String(id) },
      ],
    }),

    // ─── Workflow Requests ─────────────────────────────────────────────────────

    getWorkflowRequests: builder.query<
      IApiPaginatedResponse<IWorkflowRequest[]>,
      {
        module:       string;
        page?:        number;
        limit?:       number;
        search?:      string;
        status?:      string;
        departmentId?: number;
        startDate?:   string;
        endDate?:     string;
      }
    >({
      query: (params) => ({
        url: APIEndpoints.MySpace.workflow.getWorkflowRequests,
        method: "GET",
        params: { limit: 10, ...params },
      }),
      providesTags: ["getWorkflowRequests"],
    }),
    getAllWorkflowRequests: builder.query<
      IApiPaginatedResponse<IWorkflowRequest[]>,
      {
        /** Omit to fetch requests across all modules (shared inbox). */
        module?: string;
        page?: number;
        limit?: number;
        search?: string;
        status?: string;
      }
    >({
      query: (params) => ({
        url: APIEndpoints.MySpace.workflow.getAllWorkflowRequests,
        method: "GET",
        params,
      }),
      providesTags: ["getAllWorkflowRequests"],
    }),
    getWorkflowInstancesById: builder.query<
      IApiResponse<IWorkflowInstance[]>,
      {
        id: string;
        versionId?: string | number;
        issueFilter?: WorkflowIssueFilter;
      }
    >({
      query: ({ id, versionId, issueFilter }) => ({
        url: APIEndpoints.MySpace.workflow.getWorkflowRequests,
        method: "GET",
        params: {
          templateId: id,
          ...(versionId ? { versionId } : {}),
          ...(issueFilter ? { issueFilter } : {}),
        },
      }),
      providesTags: ["getWorkflowInstances"],
    }),
    approveWorkflowRequest: builder.mutation<IApiResponse<void>, number>({
      query: (requestId) => ({
        url: APIEndpoints.MySpace.workflow.approveWorkflowRequest(requestId),
        method: "POST",
      }),
      invalidatesTags: ["getWorkflowRequests", "getAllWorkflowRequests", "WorkflowInstance"],
    }),
    rejectWorkflowRequest: builder.mutation<
      IApiResponse<void>,
      { requestId: number; comments: string }
    >({
      query: ({ requestId, comments }) => ({
        url: APIEndpoints.MySpace.workflow.rejectWorkflowRequest(requestId),
        method: "POST",
        body: { comments },
      }),
      invalidatesTags: ["getWorkflowRequests", "getAllWorkflowRequests", "WorkflowInstance"],
    }),
    requestChangesWorkflow: builder.mutation<
      IApiResponse<void>,
      { requestId: number; comments: string; proposedStartDate?: string | null; proposedEndDate?: string | null }
    >({
      query: ({ requestId, comments, proposedStartDate, proposedEndDate }) => ({
        url: APIEndpoints.MySpace.workflow.requestChangesWorkflow(requestId),
        method: "POST",
        body: { comments, proposedStartDate, proposedEndDate },
      }),
      invalidatesTags: [
        "getWorkflowRequests",
        "getAllWorkflowRequests",
        "getWorkflowInstances",
      ],
    }),
    resubmitWorkflow: builder.mutation<IApiResponse<void>, number>({
      query: (requestId) => ({
        url: APIEndpoints.MySpace.workflow.resubmitWorkflow(requestId),
        method: "POST",
      }),
      invalidatesTags: [
        "getWorkflowRequests",
        "getAllWorkflowRequests",
        "getWorkflowInstances",
      ],
    }),
    deleteWorkflowTemplate: builder.mutation<
      IApiResponse<void>,
      { requestId: string }
    >({
      query: ({ requestId }) => ({
        url: APIEndpoints.MySpace.workflow.deleteWorkflowTemplate(requestId),
        method: "DELETE",
      }),
      invalidatesTags: [
        "getWorkflowTemplatesByModule",
        "getPaginatedWorkflowTemplates",
        "getStudioWorkflows",
        "getStudioWorkflowCounts",
      ],
    }),
    getWorkflowInstanceStats: builder.query<
      IApiResponse<IWorkflowInstanceStats>,
      { module?: string } | void
    >({
      query: (params) => ({
        url: APIEndpoints.MySpace.workflow.getWorkflowInstanceStats,
        method: "GET",
        params: params ?? {},
      }),
      providesTags: ["getWorkflowInstanceStats"],
    }),
    getWorkflowInstanceAudit: builder.query<
      IApiResponse<IWorkflowAuditEntry[]>,
      {
        instanceId: number | string;
        includeErrors?: boolean;
        eventTypes?: string[];
        actorTypes?: string[];
      }
    >({
      query: ({ instanceId, includeErrors, eventTypes, actorTypes }) => ({
        url: APIEndpoints.MySpace.workflow.getWorkflowInstanceAudit(
          instanceId,
          { includeErrors, eventTypes, actorTypes },
        ),
        method: "GET",
      }),
      providesTags: (_res, _err, { instanceId }) => [
        { type: "getWorkflowInstanceAudit", id: instanceId },
      ],
    }),
  }),
});

export const {
  useGetStudioWorkflowCountsQuery,
  useGetStudioWorkflowsQuery,
  useGetWorkflowGraphQuery,
  useLazyGetWorkflowGraphQuery,
  useUpsertWorkflowGraphMutation,
  useTestWorkflowStepMutation,
  useGetWorkflowByIdQuery,
  useGetWorkflowVersionByIdQuery,
  useGetWorkflowVersionsQuery,
  useGetWorkflowTemplatesByModuleQuery,
  useGetPaginatedWorkflowTemplatesQuery,
  useUpsertWorkflowMutation,
  usePreviewWorkflowConflictsMutation,
  useGetWorkflowRequestsQuery,
  useGetAllWorkflowRequestsQuery,
  useApproveWorkflowRequestMutation,
  useRejectWorkflowRequestMutation,
  useRequestChangesWorkflowMutation,
  useResubmitWorkflowMutation,
  useDeleteWorkflowTemplateMutation,
  useGetWorkflowInstancesByIdQuery,
  useGetWorkflowInstanceStatsQuery,
  useGetWorkflowInstanceAuditQuery,
  useGetGroupedEventTypesQuery,
  useGetScopeOptionsQuery,
  usePreviewApplicableWorkflowQuery,
  usePreviewApplicableWorkflowForEmployeeQuery,
  useActivateWorkflowTemplateMutation,
  useDeactivateWorkflowTemplateMutation,
  useGetWorkflowInstanceByEntityQuery,
} = companyWorkflowApi;
